/**
 * Ad distribution capture (marketing backlog #3, phase 1).
 *
 * Lets a marketer record where/when an approved piece ran — assessment areas + ZIP codes +
 * a date range (+ optional channel/spend) — so distribution can later be overlaid against
 * fair-lending activity for the same period on the redlining map (phase 1b).
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { MapPinIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { adDistributionApi, craApi } from '../lib/api';

interface Props {
  teamId: string;
  reportId: string;
}

const EMPTY_FORM = {
  assessmentAreaIds: [] as string[],
  zips: '',
  startDate: '',
  endDate: '',
  channel: '',
  spend: '',
  notes: '',
};

const CHANNELS = ['Direct mail', 'Social', 'Email', 'Radio', 'Print', 'Billboard', 'Digital/Display', 'Branch', 'Other'];

function countList(v: unknown): number {
  return Array.isArray(v) ? v.length : 0;
}

export default function AdDistributionPanel({ teamId, reportId }: Props) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: distData } = useQuery({
    queryKey: ['ad-distributions', teamId, reportId],
    queryFn: async () => (await adDistributionApi.list(teamId, reportId)).data,
    enabled: !!teamId && !!reportId,
  });
  const distributions: any[] = distData?.distributions ?? [];

  const { data: aaData } = useQuery({
    queryKey: ['cra-assessment-areas', teamId],
    queryFn: async () => (await craApi.listAssessmentAreas(teamId)).data,
    enabled: !!teamId && showForm,
  });
  const areas: any[] = aaData?.assessmentAreas ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ad-distributions', teamId, reportId] });

  const createMutation = useMutation({
    mutationFn: () =>
      adDistributionApi.create(teamId, reportId, {
        assessmentAreaIds: form.assessmentAreaIds,
        zips: form.zips,
        startDate: form.startDate,
        endDate: form.endDate,
        channel: form.channel || undefined,
        spendCents: form.spend ? Math.round(parseFloat(form.spend) * 100) : undefined,
        notes: form.notes || undefined,
      }),
    onSuccess: () => {
      toast.success('Distribution recorded');
      setForm(EMPTY_FORM);
      setShowForm(false);
      invalidate();
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to save distribution'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adDistributionApi.remove(teamId, id),
    onSuccess: () => { toast.success('Distribution removed'); invalidate(); },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to remove'),
  });

  const toggleArea = (id: string) =>
    setForm((f) => ({
      ...f,
      assessmentAreaIds: f.assessmentAreaIds.includes(id)
        ? f.assessmentAreaIds.filter((x) => x !== id)
        : [...f.assessmentAreaIds, id],
    }));

  const canSave =
    form.startDate && form.endDate &&
    (form.assessmentAreaIds.length > 0 || /\d{5}/.test(form.zips)) &&
    !createMutation.isPending;

  return (
    <div className="bg-white rounded-lg shadow mb-8">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPinIcon className="h-5 w-5 text-teal-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Distribution</h3>
            <p className="text-sm text-gray-600">Where &amp; when this piece ran — used for fair-lending distribution mapping.</p>
          </div>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 px-3 py-2 text-sm font-medium text-white hover:bg-teal-500"
          >
            <PlusIcon className="h-4 w-4" /> Add distribution
          </button>
        )}
      </div>

      <div className="p-6 space-y-4">
        {/* Existing records */}
        {distributions.length === 0 && !showForm && (
          <p className="text-sm text-gray-500">No distribution recorded yet. Add where this ad ran so it can be mapped against your assessment areas.</p>
        )}
        {distributions.map((d) => (
          <div key={d.id} className="flex items-start justify-between rounded-lg border border-gray-200 p-3">
            <div className="text-sm">
              <div className="font-medium text-gray-900">
                {d.channel || 'Distribution'} · {new Date(d.startDate).toLocaleDateString()} – {new Date(d.endDate).toLocaleDateString()}
              </div>
              <div className="mt-0.5 text-xs text-gray-600">
                {countList(d.assessmentAreaIds)} assessment area(s) · {countList(d.zips)} ZIP(s)
                {d.spendCents != null && <> · ${(d.spendCents / 100).toLocaleString()}</>}
              </div>
              {countList(d.zips) > 0 && (
                <div className="mt-1 text-[11px] text-gray-400">{(d.zips as string[]).slice(0, 12).join(', ')}{countList(d.zips) > 12 ? '…' : ''}</div>
              )}
              {d.notes && <div className="mt-1 text-xs italic text-gray-500">{d.notes}</div>}
            </div>
            <button
              onClick={() => deleteMutation.mutate(d.id)}
              disabled={deleteMutation.isPending}
              className="text-gray-400 hover:text-red-600"
              title="Remove"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        ))}

        {/* Add form */}
        {showForm && (
          <div className="rounded-lg border border-teal-200 bg-teal-50/40 p-4 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-xs font-medium text-gray-700">Start date</label>
                <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">End date</label>
                <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Channel</label>
                <select value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">—</option>
                  {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700">Spend (USD, optional)</label>
                <input type="number" min="0" step="0.01" value={form.spend} onChange={(e) => setForm({ ...form, spend: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="0.00" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700">Assessment areas</label>
              {areas.length === 0 ? (
                <p className="mt-1 text-xs text-gray-500">No assessment areas defined yet (optional — you can use ZIPs instead).</p>
              ) : (
                <div className="mt-1 flex flex-wrap gap-2">
                  {areas.map((a) => (
                    <button key={a.id} type="button" onClick={() => toggleArea(a.id)}
                      className={clsx('rounded-full border px-3 py-1 text-xs',
                        form.assessmentAreaIds.includes(a.id) ? 'border-teal-500 bg-teal-100 text-teal-800' : 'border-gray-300 text-gray-600')}>
                      {a.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700">ZIP codes</label>
              <textarea value={form.zips} onChange={(e) => setForm({ ...form, zips: e.target.value })} rows={2}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono"
                placeholder="38103, 38104, 38016 …" />
              <p className="mt-1 text-[11px] text-gray-400">Paste any list — commas, spaces, or new lines. We keep the unique 5-digit ZIPs.</p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700">Notes (optional)</label>
              <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => createMutation.mutate()} disabled={!canSave}
                className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-500 disabled:opacity-50">
                {createMutation.isPending ? 'Saving…' : 'Save distribution'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
